// /forgot-password — recibe email del usuario, dispara
// pb.collection('users').requestPasswordReset(email). PB envía email
// transaccional via Resend (SMTP configurado en /api/settings).
//
// Mensaje de éxito GENÉRICO (no leakear si el email existe o no — si decimos
// "email enviado" solo cuando existe, un atacante puede enumerar usuarios).

import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { ClientResponseError } from 'pocketbase';
import { ArrowLeft, CheckCircle2, Sun, Moon } from 'lucide-react';
import { pb, isLoggedIn } from '~/lib/pb';
import { applyTheme, getStoredTheme } from '~/lib/theme';
import type { Theme } from '~/lib/theme';
import styles from './login.module.css';

export const Route = createFileRoute('/forgot-password')({
  beforeLoad: () => {
    if (isLoggedIn()) {
      throw redirect({ to: '/' });
    }
  },
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [theme, setTheme] = useState<Theme>(getStoredTheme());

  function handleToggleTheme() {
    const next: Theme = theme === 'light' ? 'dark' : 'light';
    applyTheme(next);
    setTheme(next);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await pb.collection('users').requestPasswordReset(email);
      // Siempre marcamos como "enviado" aunque el email no exista — anti-enum.
      setSubmitted(true);
    } catch (err) {
      // Si PB falla (ej. SMTP caído), mostramos error técnico.
      const msg =
        err instanceof ClientResponseError
          ? err.response?.message || err.message
          : 'No pudimos enviar el email. Intenta más tarde.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.login}>
      <button
        type="button"
        className={styles.login__themeToggle}
        onClick={handleToggleTheme}
        aria-label={`Cambiar a tema ${theme === 'light' ? 'oscuro' : 'claro'}`}
      >
        {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
      </button>

      <div className={styles.login__panel}>
        <header className={styles.login__header}>
          <div className={styles.login__brand}>
            <img
              className={styles.login__brandMark}
              src="/brand/isotipo.png"
              alt="Life Focus"
              width={48}
              height={48}
            />
            <h1 className={styles.login__brandLabel}>Recuperar acceso</h1>
          </div>
          <p className={`muted ${styles.login__subtitle}`}>
            Te enviamos un link al email para que pongas una contraseña nueva.
          </p>
        </header>

        {!submitted ? (
          <form className={styles.login__form} onSubmit={handleSubmit}>
            <label className={styles.field}>
              <span className={styles.field__label}>Email</span>
              <input
                className={styles.field__input}
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoFocus
              />
            </label>

            {error && (
              <p className={styles.login__error} role="alert">
                {error}
              </p>
            )}

            <button
              type="submit"
              className={`${styles.button} ${styles['button--primary']}`}
              disabled={loading}
            >
              {loading ? 'Enviando…' : 'Enviar link de recuperación'}
            </button>

            <div className={styles.login__forgotRow}>
              <Link to="/login" className={styles.login__forgotLink}>
                <ArrowLeft size={14} aria-hidden />
                <span>Volver al login</span>
              </Link>
            </div>
          </form>
        ) : (
          <div className={styles.successBox}>
            <div className={styles.successIcon}>
              <CheckCircle2 size={28} aria-hidden />
            </div>
            <h2 className={styles.successTitle}>Revisa tu email</h2>
            <p className={styles.successText}>
              Si <strong>{email}</strong> está registrado, te enviamos un link
              para crear una nueva contraseña.
            </p>
            <p className={styles.successHint}>
              El email puede tardar 1-2 minutos. Revisa también la carpeta de
              spam.
            </p>
            <div className={styles.login__forgotRow}>
              <Link to="/login" className={styles.login__forgotLink}>
                <ArrowLeft size={14} aria-hidden />
                <span>Volver al login</span>
              </Link>
            </div>
          </div>
        )}
      </div>

      <footer className={styles.login__footer}>
        <p className="subtle">© Life Focus. Todos los derechos reservados.</p>
      </footer>
    </div>
  );
}
